/**
 * The named follow-up list used by every "go and ask these people" table on
 * Reports → Mobile Usage: the three follow-up cards (never marked attendance / barely using
 * the app / never opens a notification) and the people behind an opened blocker row.
 *
 * WHY IT IS A COMPONENT: they are the same table with different right-hand columns, and the
 * part that is easy to get wrong is the same in all of them — showing the person well enough
 * for a program manager to act (name, employee code, store), saying when that person has
 * LEFT, falling back to the app's own numeric id when the name lookup misses, and being
 * honest that a capped list is capped.
 *
 * THE ROW SHAPE IS THE API'S. `UsagePerson` below is the identity half of
 * `UsagePerson` in apps/api/src/services/posthog-usage.service.ts (the fields
 * mobile-usage-identity.service stamps on). Each card's own numbers — active days and
 * minutes, or the days a blocker hit that person — differ per card, so the table is generic
 * over them and the card declares its own columns. Do not add card-specific fields here:
 * a shared row type that lists every card's columns is how the previous version came to
 * read fields the API never sent.
 *
 * WHO IS MISSING FROM THESE ROWS IS NOT THIS TABLE'S BUSINESS. The API leaves out the people
 * 1Office says have exited, and how many it left out is printed by the CARD above the table
 * (see HiddenLeavers in MobileUsageReportPage) — one sentence per card, not one per table,
 * because a card can hold a headline count and a table and they must agree.
 *
 * NO ACTIONS COLUMN: these rows are a list to work from, not a row-level control strip,
 * so the table is a plain table (see AGENTS.md — the table-actions standard applies to
 * Actions columns, and this table deliberately has none).
 */
import type { ReactNode } from 'react';

/**
 * Who a follow-up row is about.
 *
 * `userId` is the mobile app's own numeric person id and is always present. Everything
 * else is resolved server-side from 1Office and the store roster and can be null when
 * that lookup misses — a row with no name still has to be usable, so it shows the id.
 */
export interface UsagePerson {
  /** Number on the wire; accepted as a string too, since it is only ever printed. */
  userId: number | string;
  empCode?: string | null;
  name?: string | null;
  store?: string | null;
  /** City of that store, when the roster knows it. Shown after the store. */
  city?: string | null;
  /**
   * False when 1Office says this person has exited.
   *
   * NULL/UNDEFINED MEANS UNKNOWN, NOT ACTIVE (see MobileUsageIdentity.isActive): the
   * directory may simply not have answered. So only `false` prints anything, and an unknown
   * prints nothing — telling a manager that someone has left when nobody knows is the one
   * mistake here that costs somebody their job.
   *
   * THE LABEL IS KEPT EVEN THOUGH THE API NOW HIDES LEAVERS FROM THESE LISTS. The filter
   * runs in the controller, over whatever 1Office answered for that request; a row marked as
   * an exit can still arrive — the directory answered late, the list came from a cached
   * block, a future list is wired up without the filter. When one does, the honest thing is
   * to say so rather than to show it as an ordinary person to go and ring.
   */
  isActive?: boolean | null;
}

/** A right-hand column: the number that put this person on the list. */
export interface WorklistColumn<T extends UsagePerson> {
  header: string;
  cell: (person: T) => ReactNode;
}

interface WorklistTableProps<T extends UsagePerson> {
  /** The rows to show. The API calls this field `rows`; its `people` is a COUNT. */
  people: T[];
  /** Everyone who qualifies — may be larger than `people.length`, which is capped. */
  total: number;
  columns: Array<WorklistColumn<T>>;
  /** Shown instead of the table when nobody qualifies. Good news, so say it plainly. */
  emptyMessage: string;
  /**
   * Rows before the list is capped. Defaults to ROW_CAP, which is right for a card: a
   * worklist sits inside a page someone is scrolling, and fifty names is as far as that
   * page can carry. A drill-down opens in a window of its own and is RANKED, so it shows
   * more — the number worth reading is often the twentieth row, not the first.
   */
  rowCap?: number;
  /**
   * Replaces the end of the count line when the list is capped.
   *
   * The default — "the rest are in the same situation" — is true of the three worklist
   * cards, where every row qualified the same way and the order between them means little.
   * It is NOT true of a ranked list, where the rows that were cut are precisely the ones
   * that matter least, so a ranked list says what its own cap kept.
   */
  countNote?: string;
}

/** Rows shown before the list is capped. Long enough to cover a real store cluster,
 *  short enough that the page stays readable; the count line says what is hidden. */
const ROW_CAP = 50;

/** What to call a person when the name lookup missed. Never a blank cell: a program
 *  manager can still search the app id, and a blank reads as a bug. */
const personLabel = (p: UsagePerson): string => p.name?.trim() || `App user ${p.userId}`;

/** The second line under the name: employee code, store and city, whichever resolved. */
const personDetail = (p: UsagePerson): string => {
  const place = [p.store?.trim(), p.city?.trim()].filter((s): s is string => Boolean(s)).join(', ');
  const parts = [p.empCode?.trim(), place].filter((s): s is string => Boolean(s));
  return parts.length > 0 ? parts.join(' · ') : 'Employee code and store not found';
};

/** Every one of these tables is one row per PERSON, so the count lines read in people. */
const UNIT = { one: 'person', many: 'people' } as const;

export function WorklistTable<T extends UsagePerson>({
  people, total, columns, emptyMessage, rowCap, countNote,
}: WorklistTableProps<T>) {
  // The rows come straight off an HTTP response, so a contract drift must degrade to the
  // "names could not be loaded" line below rather than throwing inside the render and
  // taking the whole report down with the route's error boundary.
  const rows = Array.isArray(people) ? people : [];

  if (rows.length === 0) {
    // "Nobody qualifies" and "somebody qualifies but no rows came back" are different
    // answers, and only the first is good news. Saying the first when the rows failed to
    // arrive would quietly hide a real list, so the counts decide which one is printed.
    return (
      <p className="rounded border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        {total > 0
          ? `${total.toLocaleString()} ${total === 1 ? UNIT.one : UNIT.many} are on this list, but the names could not be loaded this time.`
          : emptyMessage}
      </p>
    );
  }

  // A cap of 0 or a negative one is a caller bug, not an instruction to show nothing.
  const cap = typeof rowCap === 'number' && rowCap > 0 ? Math.floor(rowCap) : ROW_CAP;
  const shown = rows.slice(0, cap);
  // The count line compares against whichever is larger: a count that came back smaller
  // than the rows it shipped must not make the page claim it is showing fewer than it is.
  const qualified = Math.max(total, rows.length);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3 font-medium">Person</th>
              {columns.map((c) => (
                <th key={c.header} className="py-2 pl-3 text-right font-medium">{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* The id is the key, with the position behind it: a list is one row per person
                today, and a drifted payload that repeats one must still render. */}
            {shown.map((p, i) => (
              <tr key={`${p.userId}-${i}`} className="border-b border-gray-50 last:border-0">
                <td className="py-2 pr-3">
                  <div className="font-medium text-gray-900">{personLabel(p)}</div>
                  <div className="text-xs text-gray-500">{personDetail(p)}</div>
                  {p.isActive === false && (
                    <div className="mt-0.5 text-xs font-medium text-amber-700">No longer with the company</div>
                  )}
                </td>
                {columns.map((c) => (
                  <td key={c.header} className="py-2 pl-3 text-right tabular-nums text-gray-700 align-top">
                    {c.cell(p)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {qualified > shown.length
          ? `Showing ${shown.length} of ${qualified.toLocaleString()} ${UNIT.many}${countNote ? ` — ${countNote}` : ' — the rest are in the same situation.'}`
          : `Showing all ${qualified.toLocaleString()} ${qualified === 1 ? UNIT.one : UNIT.many}.`}
      </p>
    </div>
  );
}

export default WorklistTable;
